import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

interface HeroBanner {
  id: string;
  image: string;
  subtitle?: string;
  title: string;
  buttonText: string;
  buttonLink: string;
  size: "large" | "small";
}

interface HeroBannersProps {
  banners: HeroBanner[];
}

export const HeroBanners = ({ banners }: HeroBannersProps) => {
  const largeBanner = banners.find(b => b.size === "large");
  const smallBanners = banners.filter(b => b.size === "small");

  return (
    <section className="max-w-[1600px] mx-auto px-3 lg:px-6 py-4 lg:py-6">
      {/* Mobile Layout - Hero + 2 smaller side by side */}
      <div className="lg:hidden space-y-3">
        {/* Large Banner - Mobile */}
        {largeBanner && (
          <Link
            to={largeBanner.buttonLink}
            className="relative block rounded-2xl overflow-hidden group aspect-[4/3]"
          >
            <img
              src={largeBanner.image}
              alt={largeBanner.title}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5">
              {largeBanner.subtitle && (
                <p className="text-white/80 text-xs uppercase tracking-widest mb-1">
                  {largeBanner.subtitle}
                </p>
              )}
              <h2 className="text-2xl font-bold text-white mb-3">
                {largeBanner.title}
              </h2>
              <Button size="sm" className="bg-primary hover:bg-primary/90 font-bold">
                {largeBanner.buttonText}
              </Button>
            </div>
          </Link>
        )}

        {/* Small Banners - 2 side by side on mobile */}
        <div className="grid grid-cols-2 gap-3">
          {smallBanners.slice(0, 2).map((banner) => (
            <Link
              key={banner.id}
              to={banner.buttonLink}
              className="relative rounded-xl overflow-hidden group aspect-[3/4]"
            >
              <img
                src={banner.image}
                alt={banner.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3">
                {banner.subtitle && (
                  <p className="text-white/80 text-[10px] uppercase tracking-widest mb-0.5 line-clamp-1">
                    {banner.subtitle}
                  </p>
                )}
                <h3 className="text-sm font-bold text-white mb-2 line-clamp-2">
                  {banner.title}
                </h3>
                <span className="inline-block bg-white text-foreground text-xs font-semibold px-3 py-1.5 rounded-lg">
                  {banner.buttonText}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Desktop Layout - Original */}
      <div className="hidden lg:grid grid-cols-3 gap-4">
        {/* Large Banner */}
        {largeBanner && (
          <div className="col-span-2 relative rounded-2xl overflow-hidden group min-h-[500px]">
            <img
              src={largeBanner.image}
              alt={largeBanner.title}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-10">
              {largeBanner.subtitle && (
                <p className="text-white/80 text-sm uppercase tracking-widest mb-2">
                  {largeBanner.subtitle}
                </p>
              )}
              <h2 className="text-5xl font-bold text-white mb-6">
                {largeBanner.title}
              </h2>
              <Link to={largeBanner.buttonLink}>
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-base px-8 py-6 font-bold">
                  {largeBanner.buttonText}
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Small Banners Stack */}
        <div className="flex flex-col gap-4">
          {smallBanners.slice(0, 2).map((banner) => (
            <Link
              key={banner.id}
              to={banner.buttonLink}
              className="relative rounded-2xl overflow-hidden group flex-1"
            >
              <img
                src={banner.image}
                alt={banner.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                {banner.subtitle && (
                  <p className="text-white/80 text-xs uppercase tracking-widest mb-1">
                    {banner.subtitle}
                  </p>
                )}
                <h3 className="text-2xl font-bold text-white mb-3">
                  {banner.title}
                </h3>
                <Button className="bg-white hover:bg-white/90 text-foreground font-semibold px-6">
                  {banner.buttonText}
                </Button>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
